import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import SvgIcon from "@mui/material/SvgIcon";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { ReactComponent as MicrophoneIcon } from "../../../assets/rewamp/pwa/microphone-2.svg";
import useVoiceToText, { VoiceError, VoiceState } from "../../hooks/useVoiceToText";
import { useRewampLocalization, useRewampT } from "../../localisation";
import { inputPartSx } from "../../design/recipes/input";

const VOICE_ERROR_KEY = {
  [VoiceError.PERMISSION]: "GLOBAL_SEARCH.VOICE_PERMISSION",
  [VoiceError.NO_DEVICE]: "GLOBAL_SEARCH.VOICE_NO_DEVICE",
  [VoiceError.BUSY]: "GLOBAL_SEARCH.VOICE_BUSY",
  [VoiceError.NO_SPEECH]: "GLOBAL_SEARCH.VOICE_NO_SPEECH",
  [VoiceError.FAILED]: "GLOBAL_SEARCH.VOICE_FAILED",
};

const ERROR_ID = "global-search-voice-error";

const micLabelKey = (isRecording, isProcessing) => {
  if (isProcessing) return "GLOBAL_SEARCH.VOICE_PROCESSING";
  return isRecording ? "GLOBAL_SEARCH.VOICE_STOP" : "GLOBAL_SEARCH.VOICE_START";
};

const GlobalSearchPill = ({ anchorRef, onFocusInput, onTranscript, query, children }) => {
  useRewampLocalization(["common"]);
  const tRewamp = useRewampT();
  const voice = useVoiceToText({ onTranscript });
  const { clearError } = voice;

  useEffect(() => {
    clearError();
  }, [query, clearError]);

  const isRecording = voice.state === VoiceState.RECORDING;
  const isProcessing = voice.state === VoiceState.PROCESSING;

  const handleMicClick = (event) => {
    event.stopPropagation();
    if (isRecording) voice.stop();
    else voice.start();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box ref={anchorRef} onClick={onFocusInput} sx={inputPartSx("searchPill", "root")}>
        {children(voice.error ? ERROR_ID : undefined)}
        {voice.supported && (
          <IconButton
            size="small"
            onClick={handleMicClick}
            disabled={isProcessing}
            aria-label={tRewamp(micLabelKey(isRecording, isProcessing))}
            aria-pressed={isRecording}
            aria-busy={isProcessing}
            sx={inputPartSx("searchPill", "mic")}
          >
            {isProcessing && <CircularProgress size={20} color="inherit" aria-hidden="true" />}
            {isRecording && <StopCircleOutlinedIcon aria-hidden="true" />}
            {!isRecording && !isProcessing && <SvgIcon component={MicrophoneIcon} inheritViewBox aria-hidden="true" />}
          </IconButton>
        )}
      </Box>
      {voice.error && (
        <Typography id={ERROR_ID} role="alert" variant="caption" sx={inputPartSx("searchPill", "error")}>
          {tRewamp(VOICE_ERROR_KEY[voice.error])}
        </Typography>
      )}
    </Box>
  );
};

GlobalSearchPill.propTypes = {
  anchorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  onFocusInput: PropTypes.func.isRequired,
  onTranscript: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired,
};

export default GlobalSearchPill;
